/* One bet: what it is, and what we are doing for it.

   Edits save on blur, the same as a service page, for the same reason -- a
   Save button on a form this small mostly produces abandoned edits. Only an
   admin sees editable controls; everyone else reads the same page. */
import { useCallback, useEffect, useState } from "react";

import { api, type Bet, type StrategyOverview } from "../api";
import { usePermissions } from "../auth";
import { Chip, Field, Panel } from "../components/ui";
import { GapBadges, statusTone } from "../components/strategy";
import { go, href } from "../route";
import { useStore } from "../store";

export default function BetDetail({ id }: { id: number }) {
  const { meta, people, run, refresh } = useStore();
  const { isAdmin } = usePermissions();
  const [bet, setBet] = useState<Bet | null>(null);
  const [overview, setOverview] = useState<StrategyOverview | null>(null);
  const [missing, setMissing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, o] = await Promise.all([api.bet(id), api.strategy()]);
      setBet(b);
      setOverview(o);
      setMissing(false);
    } catch {
      setMissing(true);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (missing) return <p className="note">No such bet. It may have been deleted.</p>;
  if (!bet || !overview || !meta) return <p className="note">Loading…</p>;

  const patch = async (body: Record<string, unknown>) => {
    const done = await run(() => api.patchBet(bet.id, body));
    if (done) {
      await refresh();
      await load();
    }
  };

  const group = overview.bets.find((g) => g.bet.id === bet.id);
  const byId = new Map(overview.initiatives.map((i) => [i.id, i]));
  const members = (group?.initiative_ids ?? []).map((i) => byId.get(i)).filter(Boolean);

  return (
    <>
      <p className="crumbs">
        <a className="crumb" href={href.strategy}>
          Strategy
        </a>{" "}
        / {bet.name}
      </p>
      <div className="panel-hd" style={{ marginBottom: 14 }}>
        <h1 style={{ fontFamily: "var(--display)", fontSize: 22, margin: 0 }}>
          <span className="bcode">{bet.code}</span> {bet.name}
        </h1>
        <span className="row tight">
          <Chip text={bet.type} />
          <Chip text={bet.status} tone={statusTone(bet.status)} />
          <GapBadges gaps={group?.gaps} />
        </span>
      </div>

      <div className="detail">
        <div>
          <Panel title="Details">
            {isAdmin ? (
              <>
                <div className="row">
                  <Field label="Code">
                    <input
                      type="text"
                      defaultValue={bet.code}
                      key={`code-${bet.updated_at}`}
                      style={{ minWidth: 90 }}
                      onBlur={(e) => e.target.value !== bet.code && patch({ code: e.target.value })}
                    />
                  </Field>
                  <Field label="Name">
                    <input
                      type="text"
                      defaultValue={bet.name}
                      key={`name-${bet.updated_at}`}
                      onBlur={(e) => e.target.value !== bet.name && patch({ name: e.target.value })}
                    />
                  </Field>
                  <Field label="Type">
                    <select value={bet.type} onChange={(e) => patch({ type: e.target.value })}>
                      {meta.bet_types.map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Status">
                    <select value={bet.status} onChange={(e) => patch({ status: e.target.value })}>
                      {meta.bet_statuses.map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Owner">
                    <select
                      value={bet.owner_id ?? ""}
                      onChange={(e) =>
                        patch({ owner_id: e.target.value === "" ? null : Number(e.target.value) })
                      }
                    >
                      <option value="">Unassigned</option>
                      {people.map((p) => (
                        <option key={p.person_id} value={p.person_id}>
                          {p.display_name}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label="Description">
                  <textarea
                    rows={6}
                    defaultValue={bet.description}
                    key={`desc-${bet.id}`}
                    onBlur={(e) =>
                      e.target.value !== bet.description && patch({ description: e.target.value })
                    }
                  />
                </Field>
              </>
            ) : (
              <>
                <p className="meta-line">Owner {bet.owner_name ?? "—"}</p>
                <p className="prose">{bet.description || "No description."}</p>
              </>
            )}
          </Panel>

          <Panel
            title="Initiatives"
            aside={<span className="count">{members.length}</span>}
          >
            {members.length === 0 ? (
              <p className="note">
                Nothing serves this bet yet. Link an initiative to it from the initiative&rsquo;s
                own page.
              </p>
            ) : (
              <div className="tablewrap">
                <table>
                  <thead>
                    <tr>
                      <th className="left">Initiative</th>
                      <th className="left">Status</th>
                      <th className="left">Owner</th>
                      <th className="left">Window</th>
                      <th className="num">Services</th>
                      <th className="num">Assets</th>
                      <th className="num">Tasks</th>
                      <th className="num">Maturity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((i) => (
                      <tr key={i!.id}>
                        <td className="left">
                          <a href={href.initiative(i!.id)}>
                            <span className="icode">{i!.code}</span> {i!.name}
                          </a>
                          <GapBadges gaps={i!.gaps} />
                        </td>
                        <td className="left">
                          <Chip text={i!.status} tone={statusTone(i!.status)} />
                        </td>
                        <td className="left muted">{i!.owner_name ?? "—"}</td>
                        <td className="left muted nowrap">
                          {i!.start_quarter ?? "—"}
                          {i!.target_quarter ? ` → ${i!.target_quarter}` : ""}
                        </td>
                        <td className="num">
                          {i!.rollup!.services_active}
                          {i!.rollup!.services_idea > 0 && (
                            <span className="muted"> +{i!.rollup!.services_idea}</span>
                          )}
                        </td>
                        <td className="num">{i!.rollup!.assets}</td>
                        <td className="num">
                          {i!.rollup!.tasks_open + i!.rollup!.tasks_in_progress}
                          <span className="muted">/{i!.rollup!.tasks}</span>
                          {i!.rollup!.tasks_overdue > 0 && (
                            <span className="overdue"> {i!.rollup!.tasks_overdue}</span>
                          )}
                        </td>
                        <td className="num">
                          {i!.rollup!.maturity === null
                            ? "—"
                            : `${Math.round(i!.rollup!.maturity)}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          {isAdmin && (
            <Panel title="Danger">
              <button
                className="btn"
                onClick={async () => {
                  if (
                    !confirm(
                      `Delete the bet "${bet.name}"?\n\n` +
                        `Its ${members.length} initiative${members.length === 1 ? "" : "s"} ` +
                        `will stay in the tracker and show as not linked to a bet. ` +
                        `To keep the history instead, set the status to dropped.`,
                    )
                  ) {
                    return;
                  }
                  const done = await run(() => api.removeBet(bet.id));
                  if (done !== undefined) {
                    await refresh();
                    go(href.strategy);
                  }
                }}
              >
                Delete this bet
              </button>
            </Panel>
          )}
        </div>
      </div>
    </>
  );
}
